import { Box, FormGroup, Grid } from "@mui/material";
import React, { useEffect } from "react";
import Tooltip from "@mui/material/Tooltip";
import HelpIcon from "@mui/icons-material/Help";
import { CheckboxForAcquireBook } from "@/components/Checkbox";
import {
  type PictureTypeData,
  type BaseAcquireBookFormData,
  type AcquireBookPictures,
  type PictureTypeKey,
  type ReuseUiData,
  type PictureZenbujikoOptionKey,
} from "@/types/acquirebook";
import { useFormContext, useWatch } from "react-hook-form";
import { ReuseInformationRadioButtons } from "@/components/AcquireBook/ReuseInformationRadioButtons";

interface IPropsCheckboxesToSelectBookOptions {
  disabled: boolean;
  isDataFetched: boolean;
  getReuseData: (
    key: AcquireBookPictures,
    requestOptions: PictureTypeData
  ) => ReuseUiData;
  handleCustomOptionChange?: (event: boolean, key: PictureTypeKey) => void; // 図面チェックボックスを更新した場合に実行されるコールバック関数
}

const zenbuWithTooltip = (
  <>
    全部事項
    <Tooltip
      title="信託/共同担保目録をそれぞれ含めるか選択、現在事項は履歴情報を含めない場合に選択してください"
      placement="right"
    >
      <HelpIcon />
    </Tooltip>
  </>
);

/**
 * 登記取得モーダルや、単体登記取得画面で使用するもので、登記種別を選択するセレクトボックスを生成するReactComponent
 *
 * ルール
 * - 親コンポーネントではreact-hook-formのFormProviderを使用してform情報を設定してください
 * - 伝達するformはBaseAcquireBookFormSchemaを拡張したスキーマを使用してください
 *
 * 上記ルールとしている理由
 * - BaseAcquireBookFormSchemaを単純に引数で受け取ろうとすると呼び出し側の型定義エラーになる
 * @param props
 * @returns
 */
export const CheckboxesToSelectBookOptions: React.FC<
  IPropsCheckboxesToSelectBookOptions
> = (props) => {
  const { disabled, isDataFetched, getReuseData, handleCustomOptionChange } =
    props;

  const { control, setValue, trigger, watch } =
    useFormContext<BaseAcquireBookFormData>();

  // 閉鎖登記簿請求がチェックされたら全部事項以外のチェックボックス全部事項の現在事項とを非活性にする
  // 全部事項と信託目録、共同担保目録をチェックする
  const requestClosureBook = watch("requestClosureBook");
  const [isDisabledByRequestClosureBook, setIsDisabledByRequestClosureBook] =
    React.useState<boolean>(false);
  useEffect(() => {
    if (requestClosureBook) {
      setIsDisabledByRequestClosureBook(true);
      setValue("pictureCheckOptions.SHOYUSHAJIKO.checked", false);
      setValue("pictureCheckOptions.ZENBUJIKO.checked", true);
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked",
        true
      );
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked",
        true
      );
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.GENZAIJIKO.checked",
        false
      );
      setValue("pictureCheckOptions.CHISEKISOKURYOZU.checked", false);
      setValue("pictureCheckOptions.DENSHIKOZU.checked", false);
      setValue("pictureCheckOptions.CHIEKIKENZUMEN.checked", false);
      setValue("pictureCheckOptions.TATEMONOZUMEN.checked", false);
    } else {
      setIsDisabledByRequestClosureBook(false);
      setValue("pictureCheckOptions.ZENBUJIKO.checked", false);
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked",
        false
      );
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked",
        false
      );
    }
  }, [requestClosureBook]);

  /**
   * 親コンポーネントは再レンダリングする必要がないためuseWatchを使用
   *
   * 例：
   * - 親ページはpictureCheckOptionsをwatchしているため関係ない
   * - 親モーダルはpictureCheckOptionsをwatchしていないため、本コンポーネントのみレンダリングされる
   */
  const requestOptions = useWatch({
    control,
    name: "pictureCheckOptions",
  });

  /**
   * 図面種別のチェックボックスの状態を更新する
   * @param event
   * @param key
   */
  const handleOptionChange = (event: boolean, key: PictureTypeKey): void => {
    if (disabled) {
      // チェックボックスが無効な場合は何もしない
      return;
    }

    // チェックボックスの状態を更新
    setValue(`pictureCheckOptions.${key}.checked`, event);

    // ラジオボタンの状態を更新
    if (isDataFetched) {
      // データが取得済みの場合のみ、再利用情報のラジオボタンを更新
      setValue(`pictureCheckOptions.${key}.reuse`, event ? "REUSE_NEW" : null);
    }

    if (key === "ZENBUJIKO") {
      // 全部事項のチェックが更新された場合
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked",
        event
      );
      setValue(
        "pictureCheckOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked",
        event
      );
      if (!event) {
        // チェックが外れた場合のみ現在事項のチェックを外す
        setValue(
          "pictureCheckOptions.ZENBUJIKO.option.GENZAIJIKO.checked",
          event
        );
      }
    }

    if (handleCustomOptionChange) {
      // 呼び出し元に特化した処理がある場合はそれを実行
      handleCustomOptionChange(event, key);
    }

    // バリデーションを明示的に再実行
    void trigger("pictureCheckOptions");
  };

  /**
   * 全部事項関連のチェックボックスの状態を更新する
   * @param event
   * @param key
   */
  const handleZenbujikoOptionChange = (
    event: boolean,
    key: PictureZenbujikoOptionKey
  ): void => {
    if (disabled) {
      // チェックボックスが無効な場合は何もしない
      return;
    }

    // チェックボックスの状態を更新
    setValue(`pictureCheckOptions.ZENBUJIKO.option.${key}.checked`, event);

    // 全部事項関連のチェックが更新された場合
    if (event) {
      if (!requestOptions.ZENBUJIKO.checked) {
        // eventがtrueかつ全部事項にチェックがついていない場合はチェックを付ける
        setValue("pictureCheckOptions.ZENBUJIKO.checked", event);
        // ラジオボタンの状態を更新
        if (isDataFetched) {
          // データが取得済みの場合のみ、再利用情報のラジオボタンを更新
          setValue(
            "pictureCheckOptions.ZENBUJIKO.reuse",
            event ? "REUSE_NEW" : null
          );
        }
      }
    }

    // バリデーションを明示的に再実行
    void trigger("pictureCheckOptions");
  };

  return (
    <Box sx={{ mx: 3, mb: 3 }}>
      <FormGroup>
        <Grid container spacing={1}>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"所有者事項"}
              checked={requestOptions.SHOYUSHAJIKO.checked}
              disabled={
                requestOptions.SHOYUSHAJIKO.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleOptionChange(e, "SHOYUSHAJIKO");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.SHOYUSHAJIKO.reuse"}
              control={control}
              reuseData={getReuseData("SHOYUSHAJIKO", requestOptions)}
            />
          </Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={zenbuWithTooltip}
              checked={requestOptions.ZENBUJIKO.checked}
              disabled={requestOptions.ZENBUJIKO.disabled}
              onChange={(e) => {
                handleOptionChange(e, "ZENBUJIKO");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.ZENBUJIKO.reuse"}
              control={control}
              reuseData={getReuseData("ZENBUJIKO", requestOptions)}
            />
          </Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"信託目録"}
              sx={{ pl: "2em", py: 0 }}
              size={"small"}
              checked={requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.checked}
              disabled={
                requestOptions.ZENBUJIKO.option.SHINTAKUMOKUROKU.disabled
              }
              onChange={(e) => {
                handleZenbujikoOptionChange(e, "SHINTAKUMOKUROKU");
              }}
            />
          </Grid>
          <Grid item xs={9}></Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"共同担保目録"}
              sx={{ pl: "2em", py: 0 }}
              size={"small"}
              checked={
                requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.checked
              }
              disabled={
                requestOptions.ZENBUJIKO.option.KYODOTANPOMOKUROKU.disabled
              }
              onChange={(e) => {
                handleZenbujikoOptionChange(e, "KYODOTANPOMOKUROKU");
              }}
            />
          </Grid>
          <Grid item xs={9}></Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"現在事項"}
              sx={{ pl: "2em", py: 0 }}
              size={"small"}
              checked={requestOptions.ZENBUJIKO.option.GENZAIJIKO.checked}
              disabled={
                requestOptions.ZENBUJIKO.option.GENZAIJIKO.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleZenbujikoOptionChange(e, "GENZAIJIKO");
              }}
            />
          </Grid>
          <Grid item xs={9}></Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"地積測量図"}
              checked={requestOptions.CHISEKISOKURYOZU.checked}
              disabled={
                requestOptions.CHISEKISOKURYOZU.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleOptionChange(e, "CHISEKISOKURYOZU");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.CHISEKISOKURYOZU.reuse"}
              control={control}
              reuseData={getReuseData("CHISEKISOKURYOZU", requestOptions)}
            />
          </Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"電子公図"}
              checked={requestOptions.DENSHIKOZU.checked}
              disabled={
                requestOptions.DENSHIKOZU.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleOptionChange(e, "DENSHIKOZU");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.DENSHIKOZU.reuse"}
              control={control}
              reuseData={getReuseData("DENSHIKOZU", requestOptions)}
            />
          </Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"地役権図面"}
              checked={requestOptions.CHIEKIKENZUMEN.checked}
              disabled={
                requestOptions.CHIEKIKENZUMEN.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleOptionChange(e, "CHIEKIKENZUMEN");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.CHIEKIKENZUMEN.reuse"}
              control={control}
              reuseData={getReuseData("CHIEKIKENZUMEN", requestOptions)}
            />
          </Grid>
          <Grid item xs={3}>
            <CheckboxForAcquireBook
              label={"建物図面"}
              checked={requestOptions.TATEMONOZUMEN.checked}
              disabled={
                requestOptions.TATEMONOZUMEN.disabled ||
                isDisabledByRequestClosureBook
              }
              onChange={(e) => {
                handleOptionChange(e, "TATEMONOZUMEN");
              }}
            />
          </Grid>
          <Grid item xs={9}>
            <ReuseInformationRadioButtons
              name={"pictureCheckOptions.TATEMONOZUMEN.reuse"}
              control={control}
              reuseData={getReuseData("TATEMONOZUMEN", requestOptions)}
            />
          </Grid>
        </Grid>
      </FormGroup>
    </Box>
  );
};
